/*
  不良一覧コンポーネント
*/
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import Navbar from "../navbar/Navbar";
import "./DefectsList.css";
import "../form/ReactSelect.css";
import apiAxios from "../../utils/apiAxios";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { setDefectsList } from "../../redux/defectsListSlice";
import { appendAlert } from "../../redux/alertSlice";
import { PlantCodeSelect } from "../ui/PlantCodeSelect";
import { ProcCodeSelect } from "../ui/ProcCodeSelect";
import { MachCodeSelect } from "../ui/MachCodeSelect";
import Footer from "../footer/Footer";
import Grid from "../grid/DefectsListGrid";
import Spinner from "../ui/Spinner";
import { GridApi } from "ag-grid-enterprise";
import { GetDefectsReport } from "../../models/GetDefectsReport";

// 実績情報取得クエリパラメータ
import {
  setSelectedPlantCode as gSetSelectedPlantCode,
  setSelectedProcCode as gSetSelectedProcCode,
  setMachCode as gSetMachCode,
} from "../../redux/queryParamsSlice";

const DefectsList: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();

  const plantCode = useSelector(
    (state: RootState) => state.queryParams.selectedPlantCode
  ); // 製造工場コード
  const procCode = useSelector(
    (state: RootState) => state.queryParams.selectedProcCode
  ); // 加工コード
  const machCode = useSelector(
    (state: RootState) => state.queryParams.machCode
  ); // 機械作業コード
  const defectsList = useSelector((state: RootState) => state.defectsList.rows); // 機械作業コード

  // 初期値を設定
  const [selectPlantCode, setSelectPlantCode] = useState(plantCode); // 工場コード
  const [selectedProcCode, setSelectedProcCode] = useState(procCode); // 加工コード
  const [selectedMachCode, setSelectedMachCode] = useState(
    machCode && machCode.length === 1 ? machCode[0] : ""
  ); // 機械作業コード

  // セレクトボックスの変更を検知しstateの状態を更新
  // 工場コードの変更を検知
  const handlePlantCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(gSetSelectedPlantCode(e.target.value));
    dispatch(gSetSelectedProcCode(""));
    dispatch(gSetMachCode([]));

    setSelectPlantCode(e.target.value);
    setSelectedProcCode("");
    setSelectedMachCode("");
    fetchRowData(gridApi!, e.target.value, null, null);
  };

  // 加工コードの変更を検知
  const handleProcCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(gSetSelectedProcCode(e.target.value));
    dispatch(gSetMachCode([]));

    setSelectedProcCode(e.target.value);
    setSelectedMachCode("");
    fetchRowData(gridApi!, selectPlantCode, e.target.value, null);
  };

  // 機械作業コードの変更を検知
  const handleMachCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(gSetMachCode([e.target.value]));

    setSelectedMachCode(e.target.value);
    fetchRowData(gridApi!, selectPlantCode, selectedProcCode, e.target.value);
  };

  // 再読み込み
  const reloadDefectsList = () => {
    console.log("reloadDefectsList");
    gridApi!.setRowData([]);
    let data = defectsList.map((elem: any, index: number) => {
      return {
        ...elem,
        is_add: false,
        id: index,
      };
    });

    gridApi!.applyTransaction({ add: data });
  };

  // GridのAPI
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  // 不良情報取得
  const fetchRowData = async (
    api: GridApi,
    plant_code: string | null,
    proc_code: string | null,
    mach_code: string | null
  ) => {
    api.setRowData([]);
    dispatch(setDefectsList([]));
    if (!plant_code) {
      return;
    }
    let data = await getDefectsList(plant_code, proc_code, mach_code);
    dispatch(setDefectsList(data ?? []));
    if (data) {
      data = data.map((elem: any, index: number) => {
        return {
          ...elem,
          is_add: false,
          id: index,
        };
      });
      api.applyTransaction({ add: data });
    }
  };

  // Gridの初期化時のコールバック
  const onGridReady = (params: { api: GridApi }) => {
    setGridApi(params.api);
    (async () => {
      await fetchRowData(
        params.api,
        selectPlantCode,
        selectedProcCode,
        selectedMachCode
      );
      params.api.sizeColumnsToFit();
    })();
  };

  // 一覧データ取得中状態の管理
  const [isLoading, setIsLoading] = useState(false);

  // 不良一覧データの取得
  const getDefectsList = async (
    plant_code: string | null,
    proc_code: string | null,
    mach_code: string | null
  ) => {
    try {
      setIsLoading(true);
      const response = await apiAxios.get<GetDefectsReport[]>(
        `/defect_reports`,
        {
          params: {
            plant_code: plant_code,
            proc_code: proc_code,
            mach_code: mach_code,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error(error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "データの取得に失敗しました。" + error.message,
          displayTime: 5000,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Process Flow Manager</title>
      </Helmet>
      <Navbar />
      <div className="defectslist-container">
        <div className="defectslist-container-title">
          <img src="images/defects.svg" alt="" />
          <h1>不良一覧</h1>
        </div>

        <div className="defectslist-container-navi">
          <div className="defectslist-container-navi-item">
            <PlantCodeSelect
              label="工場"
              selectPlantCode={selectPlantCode}
              handlePlantCodeChange={handlePlantCodeChange}
            />
          </div>

          <div className="defectslist-container-navi-item">
            <ProcCodeSelect
              label="加工"
              plantCode={selectPlantCode}
              selectProcCode={selectedProcCode}
              handleProcCodeChange={handleProcCodeChange}
            />
          </div>

          <div className="defectslist-container-navi-item">
            <MachCodeSelect
              label="機械"
              plantCode={selectPlantCode}
              procCode={selectedProcCode}
              selectMachCode={selectedMachCode}
              handleMachCodeChange={handleMachCodeChange}
            />
          </div>
        </div>

        <div className="defectslist-container-grid">
          {isLoading && <Spinner />}
          <Grid
            onGridReadyParent={onGridReady}
            reloadDefectsList={reloadDefectsList}
            gridApi={gridApi}
          />
        </div>
      </div>
      {/* フッター */}
      <Footer />
    </div>
  );
};

export default DefectsList;
